import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import frenchi18n from '@assets/i18n/fr.json';
import { TermParagraph } from '@core/model/domain/termParagraph';
import { GetTerms } from '@core/usecase/getTerms';
import { SendConsent } from '@core/usecase/sendConsent';
import { TranslateModule } from '@ngx-translate/core';
import { CancelOpenBankingPopinComponent } from '@shared/components/cancel-open-banking-popin/cancel-open-banking-popin.component';
import { StickyHeaderComponent } from '@shared/components/sticky-header/sticky-header.component';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { assert } from 'ts-essentials';
import { NgClass } from '@angular/common';
import { StickyFooterComponent } from '../../../../shared/components/sticky-footer/sticky-footer.component';
import { ConditionsComponent } from './components/conditions/conditions.component';
import { ConsentTermParagraph, ConsentVM } from './consent.viewmodel';

@Component({
  selector: 'app-consent',
  templateUrl: './consent.component.html',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    StickyFooterComponent,
    StickyHeaderComponent,
    CancelOpenBankingPopinComponent,
    ConditionsComponent,
    NgClass,
  ],
})
export class ConsentComponent implements OnInit {
  consentTermParagraphs: ConsentTermParagraph[] = [];
  selectedTermParagraph: TermParagraph | undefined;
  conditionAccepted = false;
  isConditionsOpen = false;
  isCancelPopinOpen = false;
  consentVM: ConsentVM;
  cguLink: string | undefined;
  isCheckboxConsentAccepted = false;
  hasAcceptConsentBeenClickedOnce = false;
  hasAcceptConsentBeenSuccessfullyClicked = false;

  constructor(
    private storeService: StoreService,
    private processEventService: ProcessEventService,
    private tracker: TrackerService,
    private getTerms: GetTerms,
    private sendConsent: SendConsent
  ) {
    this.consentVM = new ConsentVM(
      this.storeService,
      this.processEventService,
      this.tracker,
      this.getTerms,
      this.sendConsent
    );
  }

  async ngOnInit(): Promise<void> {
    const { cguLink, termParagraphs: consentTermParagraphs } = await this.consentVM.onInit();
    this.cguLink = cguLink;
    this.consentTermParagraphs = this.consentVM.mapToConsentTermParagraph(consentTermParagraphs);
  }

  openCancelPopin() {
    this.tracker.trackClick({ click: frenchi18n.continue_with_classic_form, taggingPage: 'CONSENT' });
    this.isCancelPopinOpen = true;
  }

  closeCancelPopin() {
    this.isCancelPopinOpen = false;
  }

  openTermParagraph(termParagraphIndex: number) {
    const consentTermParagraph = this.consentTermParagraphs[termParagraphIndex];
    assert(consentTermParagraph != null, `termParagraph should be defined for index ${termParagraphIndex}`);
    this.selectedTermParagraph = this.consentVM.mapToTermParagraph(consentTermParagraph);
    this.tracker.trackClick({ click: this.selectedTermParagraph!.title, taggingPage: 'CONSENT' });
  }

  closeTermParagraph() {
    this.selectedTermParagraph = undefined;
  }

  openConditions() {
    this.tracker.trackClick({
      click:
        frenchi18n.you_can_consult_personal_data_protection_1 +
        frenchi18n.you_can_consult_personal_data_protection_2,
      taggingPage: 'CONSENT',
    });

    this.isConditionsOpen = true;
  }

  closeConditions() {
    this.isConditionsOpen = false;
  }

  async onAccepted() {
    if (!this.isCheckboxConsentAccepted) {
      this.hasAcceptConsentBeenClickedOnce = true;
      return;
    }

    this.hasAcceptConsentBeenSuccessfullyClicked = true;
    await this.consentVM.onAccepted();
  }

  onCheckboxClicked() {
    if (this.hasAcceptConsentBeenSuccessfullyClicked) {
      return;
    }

    this.isCheckboxConsentAccepted = !this.isCheckboxConsentAccepted;

    if (this.isCheckboxConsentAccepted) {
      this.hasAcceptConsentBeenClickedOnce = false;
    }
  }

  openCGU(event: Event) {
    this.tracker.trackClick({
      click:
        frenchi18n.click_accept_means_you_know_conditions_1 +
        frenchi18n.click_accept_means_you_know_conditions_2,
      taggingPage: 'CONSENT',
    });

    event.preventDefault();
    event.stopImmediatePropagation();
    window.open(this.cguLink, '_blank');
  }

  get shouldDisableAcceptButton() {
    return this.hasAcceptConsentBeenSuccessfullyClicked;
  }

  get shouldDisplayCheckboxMandatoryError() {
    return this.hasAcceptConsentBeenClickedOnce && !this.hasAcceptConsentBeenSuccessfullyClicked;
  }

  get shouldDisableCheckboxToggle() {
    return this.hasAcceptConsentBeenSuccessfullyClicked;
  }
}
